import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  saveRazorpayGateway,
  saveCcavenueGateway,
  updateGatewayStatus,
  getAdminGatewayByTenantId,
} from "../../../services/academicService";
import { useLocation } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import razorpayLogo from "../../../assets/images/razorpay-logo.png";
import ccavenueLogo from "../../../assets/images/ccavenue-logo.png";
// Strip trailing slash from the base URL once
const CLEAN_BASE_URL = "https://api.crickpay.net"; // Remove trailing slash if present
const GATEWAY_CONFIG = {
  RAZORPAY: {
    label: "Razorpay",
    fields: [
      {
        name: "keyId",
        label: "Key ID",
        type: "text",
        secret: false,
        required: true,
      },
      {
        name: "keySecret",
        label: "Key Secret",
        type: "password",
        secret: true,
        required: true,
      },
      {
        name: "webhookSecret",
        label: "Webhook Secret",
        type: "password",
        secret: true,
        required: false,
      },
    ],
    /**
     * Payload shape:
     * { name, keyId, keySecret, webhookSecret, webhookUrl, tenantId, [id], [active] }
     * `name` is auto-set — not user-entered.
     */
    buildPayload: (gateway, formData, tenantId, overrides = {}) => {
      const payload = {
        name: "RAZORPAY",
        tenantId,
        keyId: formData.keyId || "",
        keySecret: formData.keySecret || "",
        webhookSecret: formData.webhookSecret || "",
        // Razorpay webhook URL includes tenantId so Razorpay knows which
        // tenant to credit. Pattern: .../webhook/razorpay/{tenantId}
        webhookUrl: `${CLEAN_BASE_URL}/academic/v1/api/public/payments/webhook/razorpay/${tenantId}`,
        active:
          overrides.active !== undefined ? overrides.active : gateway.active,
      };
      if (gateway.id) payload.id = gateway.id;
      return payload;
    },
  },

  CCAVENUE: {
    label: "CCAvenue",
    fields: [
      {
        name: "merchantId",
        label: "Merchant ID",
        type: "text",
        secret: false,
        required: true,
      },
      {
        name: "accessCode",
        label: "Access Code",
        type: "text",
        secret: false,
        required: true,
      },
      {
        name: "workingKey",
        label: "Working Key",
        type: "password",
        secret: true,
        required: true,
      },
    ],
    /**
     * Payload shape:
     * { name, tenantId, merchantId, accessCode, workingKey, redirectUrl, cancelUrl, [id], [active] }
     * `name` is auto-set — not user-entered.
     */
    buildPayload: (gateway, formData, tenantId, overrides = {}) => {
      const payload = {
        name: "CCAVENUE",
        tenantId,
        merchantId: formData.merchantId || "",
        accessCode: formData.accessCode || "",
        workingKey: formData.workingKey || "",
        // CCAvenue return URLs — backend routes the response back here
        redirectUrl: `${CLEAN_BASE_URL}/academic/v1/api/public/payments/webhook/ccavenue/redirect`,
        cancelUrl: `${CLEAN_BASE_URL}/academic/v1/api/public/payments/webhook/ccavenue/cancel`,
        active:
          overrides.active !== undefined ? overrides.active : gateway.active,
      };
      if (gateway.id) payload.id = gateway.id;
      return payload;
    },
  },
};

const GATEWAY_TYPES = ["RAZORPAY", "CCAVENUE"];

const matchGatewayType = (item) => {
  const raw = String(item?.name || item?.gatewayType || "").toUpperCase();
  if (raw.includes("RAZORPAY")) return "RAZORPAY";
  if (raw.includes("CCAVENUE") || raw.includes("CC AVENUE")) return "CCAVENUE";
  return null;
};

// tenantId is passed at call-time so URLs are always built with the
const getDisplayUrls = (gatewayType, tenantId) => {
  if (gatewayType === "RAZORPAY") {
    return [
      {
        label: "Webhook URL",
        // Pattern: .../webhook/razorpay/{tenantId}
        value: `${CLEAN_BASE_URL}/academic/v1/api/public/payments/webhook/razorpay/${tenantId}`,
      },
    ];
  }
  if (gatewayType === "CCAVENUE") {
    return [
      {
        label: "Redirect URL",
        value: `${CLEAN_BASE_URL}/academic/v1/api/public/payments/ccavenue/redirect/${tenantId}`,
      },
      {
        label: "Cancel URL",
        value: `${CLEAN_BASE_URL}/academic/v1/api/public/payments/ccavenue/cancel/${tenantId}`,
      },
    ];
  }
  return [];
};

// Single source of truth for the 3 possible states, so the badge and
// anything else that needs to describe status stay consistent.
const getGatewayStatus = (gateway) => {
  if (!gateway.configured) {
    return { key: "not_configured", label: "Not Configured" };
  }
  return gateway.active
    ? { key: "active", label: "Active" }
    : { key: "disabled", label: "Disabled" };
};

const PaymentGatewayAdmin = () => {
  const location = useLocation();
  const tenant = location.state?.tenant || null;
  const [gateways, setGateways] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [togglingType, setTogglingType] = useState(null);

  const loadGateways = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getAdminGatewayByTenantId(tenant?.id);
      const merged = { RAZORPAY: {}, CCAVENUE: {} };

      (list || []).forEach((item) => {
        const type = matchGatewayType(item);
        if (!type) return;
        merged[type] = {
          ...merged[type],
          ...item,
          gatewayType: type,
          configured: true,
          active: typeof item.active === "boolean" ? item.active : true,
        };
      });

      setGateways(merged);
    } catch (error) {
      console.error("Gateway load error:", error);
      toast.error("Unable to load payment gateways");
      setGateways({ RAZORPAY: {}, CCAVENUE: {} });
    } finally {
      setLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  /* ----------------------------------------------------------
     START EDIT
  ---------------------------------------------------------- */

  const handleConfigure = (gateway) => {
    setEditingType(gateway.gatewayType);
    setFormData({ ...gateway });
  };

  /* ----------------------------------------------------------
     CANCEL EDIT
  ---------------------------------------------------------- */

  const handleCancel = () => {
    setEditingType(null);
    setFormData({});
  };

  /* ----------------------------------------------------------
     INPUT CHANGE
  ---------------------------------------------------------- */

  const handleChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleSave = async (gateway) => {
    const config = GATEWAY_CONFIG[gateway.gatewayType];
    if (!config) return;

    for (const field of config.fields) {
      if (field.required && !formData[field.name]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    const payload = config.buildPayload(gateway, formData, tenant?.id);

    try {
      setSaving(true);

      // Route to the correct API based on gateway type
      if (gateway.gatewayType === "RAZORPAY") {
        await saveRazorpayGateway(payload);
      } else if (gateway.gatewayType === "CCAVENUE") {
        await saveCcavenueGateway(payload);
      }

      toast.success(`${config.label} configuration saved`);
      setEditingType(null);
      setFormData({});
      await loadGateways();
    } catch (error) {
      console.error("Gateway save error:", error);
      toast.error(
        error?.response?.data?.errorMessage || `Unable to save ${config.label}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (gateway) => {
    const label =
      GATEWAY_CONFIG[gateway.gatewayType]?.label || gateway.gatewayType;

    // Guard: must be configured before enabling/disabling
    if (!gateway.configured) {
      toast.error(
        `Please enter ${label} details first before enabling or disabling.`,
      );
      return;
    }

    if (!gateway.id) {
      toast.error(
        `${label} gateway ID not found. Please save configuration first.`,
      );
      return;
    }

    const nextActive = !gateway.active;

    try {
      setTogglingType(gateway.gatewayType);
      // Only the gateway id is passed — backend handles the status flip
      await updateGatewayStatus(gateway.id);
      toast.success(
        `${label} ${nextActive ? "enabled" : "disabled"} successfully`,
      );
      await loadGateways();
    } catch (error) {
      console.error("Gateway toggle error:", error);
      toast.error(
        error?.response?.data?.errorMessage ||
          `Unable to ${nextActive ? "enable" : "disable"} ${label}`,
      );
    } finally {
      setTogglingType(null);
    }
  };

  /* ----------------------------------------------------------
     MASK
  ---------------------------------------------------------- */

  const maskValue = (value) => {
    if (!value) return <span className="text-muted">Not configured</span>;
    return (
      <>
        {String(value).substring(0, 8)}
        {"•".repeat(8)}
      </>
    );
  };

  /* ----------------------------------------------------------
     COPY
  ---------------------------------------------------------- */

  // Copies the raw (unmasked) value to clipboard regardless of whether
  // the field is displayed masked (secret) or plain.
  const handleCopyField = (label, value) => {
    if (!value) {
      toast.error(`${label} is not configured`);
      return;
    }
    navigator.clipboard
      .writeText(String(value))
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error(`Unable to copy ${label}`));
  };

  /* ----------------------------------------------------------
     RENDER
  ---------------------------------------------------------- */

  return (
    <>
      <PageTitle activeMenu="Payment Gateway" motherMenu="Settings" />

      <div className="mb-3">
        <h4 className="mb-1">Payment Gateways</h4>
        <small className="text-muted">
          Configure and manage payment gateways for{" "}
          <strong>{tenant?.name || "this tenant"}</strong>
        </small>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading gateways...
        </div>
      ) : (
        <div className="row">
          {GATEWAY_TYPES.map((type) => {
            // Always inject `gatewayType` — gateways[type] may be {} (empty
            // object, truthy) so the || fallback would not fire, leaving
            // gatewayType undefined and breaking handleConfigure.
            const gateway = {
              gatewayType: type,
              configured: false,
              active: false,
              ...(gateways[type] || {}),
            };
            const config = GATEWAY_CONFIG[type];
            const isEditing = editingType === type;
            const status = getGatewayStatus(gateway);

            return (
              <div className="col-12 mb-3" key={type}>
                <div className="card">
                  {/* ---- HEADER ---- */}
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center me-4">
                        <GatewayIcon gatewayType={type} />

                        <div className="ms-3">
                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <h5 className="mb-0">{config.label}</h5>

                            {/* Status badge — the single, unambiguous
                                place to see Active / Disabled /
                                Not Configured, regardless of the
                                Enable/Disable button's label. */}
                            <span
                              className={`badge rounded-pill d-inline-flex align-items-center gap-1 px-2 py-1 ${
                                status.key === "active"
                                  ? "bg-success-subtle text-success border border-success-subtle"
                                  : status.key === "disabled"
                                    ? "bg-danger-subtle text-danger border border-danger-subtle"
                                    : "bg-secondary-subtle text-secondary border border-secondary-subtle"
                              }`}
                            >
                              <i
                                className="fa fa-circle"
                                style={{ fontSize: 7 }}
                              />
                              {status.label}
                            </span>
                          </div>

                          <small className="text-muted">
                            {gateway.configured
                              ? status.key === "active"
                                ? "Live — accepting payments"
                                : "Saved, but currently disabled"
                              : "Add your credentials to get started"}
                          </small>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        {!isEditing && (
                          <>
                            {/* Enable (green) / Disable (red)
                                Disabled + greyed when gateway has no saved data.
                                Clicking while not configured shows a toast error. */}
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                !gateway.configured
                                  ? "btn-secondary" // greyed out
                                  : gateway.active
                                    ? "btn-danger" // red  — click to Disable
                                    : "btn-success" // green — click to Enable
                              }`}
                              onClick={() => handleToggle(gateway)}
                              disabled={togglingType === type}
                              title={
                                !gateway.configured
                                  ? "Configure gateway details first"
                                  : gateway.active
                                    ? "Click to disable this gateway"
                                    : "Click to enable this gateway"
                              }
                            >
                              {togglingType === type ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : (
                                <i className="fa fa-power-off me-1" />
                              )}
                              {gateway.active ? "Disable" : "Enable"}
                            </button>

                            {/* Configure button */}
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleConfigure(gateway)}
                            >
                              <i className="fa fa-cog me-1" />
                              Configure
                            </button>
                          </>
                        )}

                        {isEditing && (
                          <>
                            <button
                              type="button"
                              className="btn btn-light btn-sm"
                              onClick={handleCancel}
                              disabled={saving}
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleSave(gateway)}
                              disabled={saving}
                            >
                              {saving ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save me-1" />
                                  Save
                                </>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ---- BODY ---- */}
                  <div className="card-body">
                    {/* Decoy fields to prevent browser autofill */}
                    <input
                      type="text"
                      name="fake-username"
                      autoComplete="username"
                      style={{ display: "none" }}
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                    <input
                      type="password"
                      name="fake-password"
                      autoComplete="new-password"
                      style={{ display: "none" }}
                      tabIndex={-1}
                      aria-hidden="true"
                    />

                    {/* Info banner: name is auto-managed */}
                    {isEditing && (
                      <div className="alert alert-light border mb-3 py-2 px-3">
                        <small className="text-muted">
                          <i className="fa fa-info-circle me-1 text-primary" />
                          Gateway name is automatically set to{" "}
                          <strong>&quot;{config.label}&quot;</strong> — no need
                          to enter it manually.
                        </small>
                      </div>
                    )}

                    <div className="row">
                      {config.fields.map((field) => (
                        <div
                          className="col-xl-3 col-lg-4 col-md-6 mb-3"
                          key={field.name}
                        >
                          <label className="form-label text-muted small">
                            {field.label}
                            {field.required && (
                              <span className="text-danger ms-1">*</span>
                            )}
                          </label>

                          {isEditing ? (
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type={field.type || "text"}
                                name={`${type}-${field.name}`}
                                id={`${type}-${field.name}`}
                                className="form-control"
                                value={formData[field.name] || ""}
                                onChange={(e) =>
                                  handleChange(field.name, e.target.value)
                                }
                                placeholder={`Enter ${field.label}`}
                                autoComplete={
                                  field.type === "password"
                                    ? "new-password"
                                    : "off"
                                }
                                data-lpignore="true"
                                data-1p-ignore="true"
                              />

                              {/* Copy icon — copies whatever is currently
                                  typed in this input */}
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm py-1 px-2 flex-shrink-0"
                                title={`Copy ${field.label}`}
                                onClick={() =>
                                  handleCopyField(
                                    field.label,
                                    formData[field.name],
                                  )
                                }
                              >
                                <i className="fa fa-copy" />
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <div className="fw-medium text-break">
                                {field.secret
                                  ? maskValue(gateway[field.name])
                                  : gateway[field.name] || (
                                      <span className="text-muted">
                                        Not configured
                                      </span>
                                    )}
                              </div>

                              {/* Copy icon — copies the real (unmasked)
                                  value even for secret fields */}
                              {/* {gateway[field.name] && (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm py-0 px-1"
                                  title={`Copy ${field.label}`}
                                  onClick={() =>
                                    handleCopyField(
                                      field.label,
                                      gateway[field.name],
                                    )
                                  }
                                >
                                  <i className="fa fa-copy" />
                                </button>
                              )} */}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* AUTO-GENERATED READ-ONLY URLS */}
                    {getDisplayUrls(type, tenant?.id).map((urlInfo) => (
                      <div className="mt-2" key={urlInfo.label}>
                        <label className="form-label text-muted small">
                          {urlInfo.label}
                          <span className="badge bg-light text-secondary ms-2 border">
                            Auto-generated
                          </span>
                        </label>

                        <div className="d-flex align-items-center">
                          <div className="bg-light border rounded p-2 flex-grow-1 text-break">
                            <code>{urlInfo.value}</code>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-primary ms-2"
                            title={`Copy ${urlInfo.label}`}
                            onClick={() =>
                              navigator.clipboard
                                .writeText(urlInfo.value)
                                .then(() =>
                                  toast.success(`${urlInfo.label} copied`),
                                )
                            }
                          >
                            <i className="fa fa-copy" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ---- FOOTER ---- */}
                  {!isEditing && (
                    <div className="card-footer d-flex justify-content-between">
                      <small className="text-muted">
                        {gateway?.updateOn
                          ? `Updated ${gateway?.updateOn ? new Date(gateway?.updateOn + "Z").toLocaleString() : "-"}`
                          : "Not configured yet"}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

/* ICON */
const GatewayIcon = ({ gatewayType }) => {
  if (gatewayType === "RAZORPAY") {
    return (
      <div
        className="rounded-circle d-flex align-items-center justify-content-center overflow-hidden flex-shrink-0"
        style={{
          width: 48,
          height: 48,
          background: "#e8f0ff",
        }}
      >
        <img
          src={razorpayLogo}
          alt="Razorpay"
          style={{
            width: 34,
            height: 34,
            objectFit: "contain",
            // The source logo has its own white/square background baked
            // in (not transparent). multiply blends white pixels into
            // whatever is behind them — i.e. this circle's background
            // color — so the white square disappears and only the dark
            // logo mark stays visible.
            mixBlendMode: "multiply",
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center overflow-hidden flex-shrink-0"
      style={{
        width: 48,
        height: 48,
        background: "#fff1e8",
      }}
    >
      <img
        src={ccavenueLogo}
        alt="CCAvenue"
        style={{
          width: 38,
          height: 38,
          objectFit: "contain",
          mixBlendMode: "multiply",
        }}
      />
    </div>
  );
};

export default PaymentGatewayAdmin;
